<script lang="ts" setup>
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'
import { useUserStore } from '@/store/user/user'

import HeaderBurger from '@/components/Header/HeaderBurger.vue'
import HeaderLogo from '@/components/Header/HeaderLogo.vue'
import SupportDialog from '@/components/Header/HeaderNavigationDrawer/SupportDialog.vue'

import { throttle } from 'throttle-debounce'

const DrawerContent = defineAsyncComponent(
  () => import('@/components/Header/HeaderNavigationDrawer/DrawerContent.vue'),
)

const HEADER_HEIGHT = 128

const { isTablet } = useResponsive()
const route = useRoute()

const { cityInfo } = useCityStore()
const { isCategoryBlockScrolling } = storeToRefs(useAppStore())
const { isAuthenticated } = storeToRefs(useUserStore())
const { newAddressFlowEnabled, isLandingPageEnabled } = useToggleStore()

const emit = defineEmits(['on-toggle-navigation'])

const isDialogCallSupportShow = ref(false)
const isCitySelectListShow = ref(false)
const showNavigation = ref(false)

let categoriesElement: HTMLElement | null = null
let lastScrollPosition = 0

const isHidden = ref(false)

const componentClasses = computed(() => {
  return [
    'header-mobile',
    { 'header-mobile--feature': newAddressFlowEnabled },
    { 'header-mobile--hidden': isHidden.value },
  ]
})

const onToggleNavigation = () => {
  showNavigation.value = !showNavigation.value

  emit('on-toggle-navigation', showNavigation.value)
}

const onProfileClick = () => {
  const url = isAuthenticated.value ? '/profile/orders/' : '/login/'
  navigateTo(url)
}

const selectCategoriesElement = () => {
  if (!isLandingPageEnabled) return

  categoriesElement =
    route.name === 'index' ? document.querySelector('.page__categories') : null
}

const onScroll = throttle(300, () => {
  if (route.name !== 'index' || !categoriesElement) return

  // Запрет проверки скролла при переходу по меню категорий
  if (isCategoryBlockScrolling.value) return

  // Запрет проверки скролла при активном ДО
  if (document.documentElement.classList.contains('ui-scroll-locked')) return

  const { scrollY } = window

  const categoriesElementTop = categoriesElement.getBoundingClientRect().top

  // Если направление скролла вниз и мы достигли элемента с категориями, то скрываем шапку

  const isBottomScroll =
    scrollY > lastScrollPosition && categoriesElementTop <= HEADER_HEIGHT

  if (isBottomScroll) {
    isHidden.value = true
    categoriesElement.style.top = '0'
  } else {
    isHidden.value = false
    categoriesElement.style.top = `${HEADER_HEIGHT}px`
  }

  lastScrollPosition = scrollY <= 0 ? 0 : scrollY
})

watch(
  () => route.name,
  () => selectCategoriesElement(),
)

onMounted(() => {
  if (isLandingPageEnabled) {
    window.addEventListener('scroll', onScroll)

    selectCategoriesElement()
  }
})

onUnmounted(() => {
  if (isLandingPageEnabled) window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <header :class="componentClasses">
    <div class="header-mobile__top">
      <header-logo />

      <header-city-select
        v-if="cityInfo.padeg?.imen"
        :is-city-select-list-show="isCitySelectListShow"
        :city-name="cityInfo.padeg.imen"
      />

      <div class="header-mobile__actions">
        <the-button
          v-if="cityInfo.is_active"
          variant="transparent"
          icon="bubble-chat"
          size="sm"
          radius="lg"
          aria-label="Открыть чат с поддержкой"
          @click="isDialogCallSupportShow = !isDialogCallSupportShow"
        />

        <the-button
          v-if="isTablet"
          variant="transparent"
          icon="user--outline"
          size="sm"
          radius="lg"
          aria-label="Перейти в личный кабинет"
          @click="onProfileClick"
        />

        <header-burger
          :is-active="showNavigation"
          @click="onToggleNavigation"
        />
      </div>
    </div>

    <header-delivery-widget v-if="newAddressFlowEnabled" />

    <the-dialog v-model="isDialogCallSupportShow">
      <support-dialog />
    </the-dialog>
  </header>

  <the-navigation-drawer v-model="showNavigation" top="60" overlay>
    <drawer-content />
  </the-navigation-drawer>
</template>

<style scoped lang="scss">
.header-mobile {
  position: sticky;
  top: 0;
  z-index: 1005;
  display: flex;
  flex-direction: column;
  grid-template-columns: max-content 1fr max-content;
  gap: 0 20px;
  align-items: flex-start;
  width: 100%;
  padding: 0 12px 0 20px;
  background: var(--ui-white);
  border-bottom: 1px solid #f1f4f9;
  transition: top 0.3s ease-in;
  will-change: top;

  &--feature {
    padding: 10px 20px 18px 20px;
  }

  &__top {
    z-index: 1005;
    display: flex;
    gap: 0 8px;
    align-items: center;
    width: 100%;
    height: 60px;
  }

  &__actions {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  &--hidden {
    top: -129px;
  }
}
</style>
